import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { showApiError } from '@/lib/utils/notifications'
import { PmsAuditLogSection } from './PmsAuditLogSection'
import { fetchPropertyMappingAudit, fetchPropertyMappingReview } from '../services/propertyMappingService'
import type {
  LiveYardiProperty,
  PropertyMappingRow,
  PropertyMatchCandidate,
} from '../types'

function formatStatus(status: string): string {
  if (status === 'MAPPED') return 'Mapped'
  if (status === 'SKIPPED') return 'Skipped'
  if (status === 'UNMAPPED') return 'Unmapped – No Yardi Property Found'
  if (status === 'NEEDS_REVIEW') return 'Needs review'
  return status.replace(/_/g, ' ').toLowerCase()
}

function PropertyDetailCard({
  title,
  name,
  subtitle,
  fields,
  muted,
}: {
  title: string
  name: string
  subtitle?: string
  fields: Array<[string, string]>
  muted?: boolean
}) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[280px] w-full flex-col rounded-[18px] border border-[#e4ebf7] bg-white p-5 shadow-sm',
        !muted && 'bg-gradient-to-b from-[#f7fbff] to-white',
      )}
    >
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">{title}</p>
      <h3 className="mb-1 line-clamp-2 min-h-[56px] text-lg font-bold leading-7 text-[#16213e]">{name}</h3>
      <p className="mb-4 line-clamp-2 min-h-[32px] text-xs leading-4 text-[#627089]">{subtitle || '--'}</p>
      <dl className="mt-auto grid flex-1 grid-rows-5 gap-1 text-xs leading-6 text-[#51607a]">
        {fields.map(([label, value]) => (
          <div key={label} className="flex min-h-[24px] gap-1">
            <dt className="shrink-0">{label}:</dt>
            <dd className="min-w-0 break-words">{value || '--'}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function PropertyMappingReviewDialogBody({
  row,
  onClose,
}: {
  row: PropertyMappingRow
  onClose: () => void
}) {
  const [yardiProperty, setYardiProperty] = useState<LiveYardiProperty | null>(null)
  const [candidates, setCandidates] = useState<PropertyMatchCandidate[]>([])
  const [loading, setLoading] = useState(true)

  useBodyScrollLock(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setYardiProperty(null)
    setCandidates([])
    void fetchPropertyMappingReview(row.id)
      .then((result) => {
        if (cancelled) return
        setYardiProperty(result.yardiProperty)
        setCandidates(result.candidates)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [row])

  const yardi: LiveYardiProperty = {
    propertyCode: yardiProperty?.propertyCode || row.sourcePropertyCode || '',
    propertyName: yardiProperty?.propertyName || row.sourcePropertyName || '',
    address: yardiProperty?.address || row.sourceAddress || '',
    address1: yardiProperty?.address1,
    address2: yardiProperty?.address2,
    city: yardiProperty?.city,
    state: yardiProperty?.state,
    postalCode: yardiProperty?.postalCode,
    live: Boolean(yardiProperty?.live),
  }

  const mappedCandidate = useMemo(() => {
    if (!row.libertyPropertyId) return null
    const scored = candidates.find((candidate) => candidate.propertyId === row.libertyPropertyId)
    if (scored) return scored
    return {
      propertyId: row.libertyPropertyId,
      propertyName: row.libertyPropertyName || `Property #${row.libertyPropertyId}`,
      address: row.libertyAddress || '',
      nameMatch: false,
      score: row.confidence ?? 0,
      reasons: [],
    } satisfies PropertyMatchCandidate
  }, [candidates, row])

  return createPortal(
    <div
      className="fixed inset-0 z-[10030] flex items-center justify-center bg-[#091430]/55 p-3 backdrop-blur-[3px] md:p-6"
      onClick={onClose}
      data-testid="property-mapping-review-overlay"
    >
      <aside
        className="relative flex h-full max-h-[calc(100vh-1.5rem)] w-full max-w-[1280px] flex-col overflow-hidden rounded-[20px] border border-[#e4ebf7] bg-gradient-to-b from-[#fbfcff] to-[#f3f7ff] shadow-[0_28px_70px_rgba(15,23,42,0.2)] md:max-h-[calc(100vh-3rem)]"
        onClick={(event) => event.stopPropagation()}
        data-testid="property-mapping-review-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-mapping-review-title"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#e4ebf7] bg-white px-6 py-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
              Detailed Review
            </p>
            <h3 id="property-mapping-review-title" className="mt-0.5 text-lg font-bold text-[#16213e]">
              {yardi.propertyName}
            </h3>
            <p className="mt-1 text-xs text-[#70809b]">
              Yardi {yardi.propertyCode || '--'} ·{' '}
              {row.status === 'NEEDS_REVIEW' && row.alreadyMapped
                ? 'Mapped · Needs review'
                : formatStatus(row.status)}
              {yardi.live ? ' · Live from Yardi' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#d5e0f1] bg-white"
            aria-label="Close detailed review"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[240px] items-center justify-center text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Fetching live Yardi property…
            </div>
          ) : (
            <div data-testid="property-mapping-review-body">
              <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)]">
                {mappedCandidate ? (
                  <PropertyDetailCard
                    title="Liberty Rent Property"
                    name={mappedCandidate.propertyName}
                    subtitle={mappedCandidate.address || 'No address'}
                    fields={[
                      ['Property ID', String(mappedCandidate.propertyId)],
                      ['Address', mappedCandidate.address || '--'],
                      ['Name Match', mappedCandidate.flags?.nameExact ? 'Exact' : mappedCandidate.flags?.nameNear ? 'Near' : 'Different'],
                      ['Score', `${mappedCandidate.score}/160`],
                      ['PMC', row.pmcName || '--'],
                    ]}
                  />
                ) : (
                  <PropertyDetailCard
                    title="Liberty Rent Property"
                    name="Unmapped"
                    subtitle="No Liberty Rent property assigned"
                    fields={[
                      ['Property ID', '--'],
                      ['Address', '--'],
                      ['Name Match', 'No city/state candidate'],
                      ['Score', '0/160'],
                      ['PMC', row.pmcName || '--'],
                    ]}
                  />
                )}
                <div className="mx-auto flex h-full min-h-[72px] w-[72px] items-center justify-center self-center">
                  <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#dce6fb] bg-gradient-to-br from-[#e6efff] to-white px-2 text-center text-[10px] font-bold text-[#3151d3]">
                    Best Yardi Match
                  </div>
                </div>
                <PropertyDetailCard
                  title="Yardi Property Record"
                  name={yardi.propertyName || row.sourcePropertyName}
                  subtitle={yardi.address || row.sourceAddress || 'No address'}
                  muted
                  fields={[
                    ['Yardi Code', yardi.propertyCode || row.sourcePropertyCode],
                    ['City', yardi.city || '--'],
                    ['State', yardi.state || '--'],
                    ['ZIP', yardi.postalCode || '--'],
                    ['PMC', row.pmcName || '--'],
                  ]}
                />
              </div>

              <div className="mt-5 space-y-5">
                {row.matchReason ? (
                  <p className="text-[11px] text-[#70809b]">
                    <span className="font-semibold">Reason:</span> {row.matchReason}
                  </p>
                ) : null}

                <div>
                  <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
                    Candidate Ranking &amp; Score Breakdown
                  </h4>
                  <div className="grid gap-4 rounded-[18px] border border-[#e4ebf7] bg-white p-[18px] sm:grid-cols-2">
                    {[
                      {
                        label: 'Name',
                        value: mappedCandidate?.flags?.nameExact ? 60 : mappedCandidate?.flags?.nameNear ? 25 : 0,
                        max: 60,
                      },
                      {
                        label: 'Alias',
                        value: mappedCandidate?.flags?.aliasExact ? 40 : mappedCandidate?.flags?.aliasNear ? 15 : 0,
                        max: 40,
                      },
                      {
                        label: 'Address',
                        value: mappedCandidate?.flags?.addressExact ? 35 : mappedCandidate?.flags?.addressNear ? 15 : 0,
                        max: 35,
                      },
                      {
                        label: 'Street number',
                        value: mappedCandidate?.flags?.streetMatch ? 10 : 0,
                        max: 10,
                      },
                      {
                        label: 'City',
                        value: mappedCandidate?.flags?.cityMatch ? 5 : 0,
                        max: 5,
                      },
                      {
                        label: 'State',
                        value: mappedCandidate?.flags?.stateMatch ? 5 : 0,
                        max: 5,
                      },
                      {
                        label: 'ZIP',
                        value: mappedCandidate?.flags?.zipMatch ? 5 : 0,
                        max: 5,
                      },
                      {
                        label: 'Overall score',
                        value: mappedCandidate?.score ?? 0,
                        max: 160,
                      },
                    ].map((entry) => (
                      <div key={entry.label}>
                        <div className="mb-2 flex justify-between text-[11px] text-[#51607a]">
                          <span>{entry.label}</span>
                          <strong>
                            {entry.value}/{entry.max}
                          </strong>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-[#e8eef9]">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#27b36a] to-[#59cf86]"
                            style={{ width: `${Math.min((entry.value / entry.max) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
                    Top Ranked Liberty Properties
                  </h4>
                  {candidates.length === 0 ? (
                    <div className="rounded-[18px] border border-dashed border-[#d7e2f2] bg-white p-6 text-sm text-[#70809b]">
                      No Liberty property in this company matches the normalized city and state.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {candidates.slice(0, 5).map((candidate, index) => (
                        <div
                          key={candidate.propertyId}
                          className="rounded-[18px] border border-[#e4ebf7] bg-white p-[18px]"
                        >
                          <div className="mb-3 flex justify-between text-[11px] font-bold text-[#51607a]">
                            <span>Candidate {index + 1}</span>
                            <strong>{candidate.score}/160</strong>
                          </div>
                          <div className="rounded-xl border border-[#e4ebf7] bg-[#f8faff] p-3.5">
                            <h5 className="font-bold text-[#16213e]">{candidate.propertyName}</h5>
                            <p className="text-xs text-[#70809b]">{candidate.address || 'No address'}</p>
                            <p className="mt-2 text-xs leading-5 text-[#51607a]">
                              Property ID: {candidate.propertyId}
                              <br />
                              {(candidate.reasons || []).join(', ') ||
                                (candidate.nameMatch ? 'Exact name match' : 'No name match')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <PmsAuditLogSection
                  variant="history"
                  testId="property-mapping-review-audit"
                  reloadKey={row.id}
                  loadPage={(page, perPage) =>
                    fetchPropertyMappingAudit({
                      mapping_id: row.id,
                      pmc_id: row.pmcId,
                      page,
                      per_page: perPage,
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  )
}

export function PropertyMappingReviewDialog({
  row,
  onClose,
}: {
  row: PropertyMappingRow | null
  onClose: () => void
}) {
  if (!row) return null
  return <PropertyMappingReviewDialogBody key={row.id} row={row} onClose={onClose} />
}
